import React, { useState, useRef, useCallback } from 'react';
import { PUZZLES } from '../data/puzzles';
import { ITEMS } from '../data/items';
import { useGameStore, useGameActions } from '../hooks/useGameStore';

export const PuzzleModal: React.FC = () => {
  const state = useGameStore();
  const actions = useGameActions();
  const [input, setInput] = useState('');
  const [hintIndex, setHintIndex] = useState(-1);
  const [showResult, setShowResult] = useState(false);
  const [resultText, setResultText] = useState('');
  const [isCorrect, setIsCorrect] = useState(false);
  const [shakeWrong, setShakeWrong] = useState(false);
  const [hintProgress, setHintProgress] = useState(0);
  const hintTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hintIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const clearHintTimer = useCallback(() => {
    if (hintTimerRef.current) {
      clearTimeout(hintTimerRef.current);
      hintTimerRef.current = null;
    }
    if (hintIntervalRef.current) {
      clearInterval(hintIntervalRef.current);
      hintIntervalRef.current = null;
    }
    setHintProgress(0);
  }, []);

  const startHintTimer = useCallback(() => {
    clearHintTimer();
    // ヒント3（次がindex 2）の場合は10秒、それ以外は5秒
    const nextIndex = hintIndex + 1;
    const duration = nextIndex === 2 ? 10000 : 5000;
    const startTime = Date.now();
    hintIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      setHintProgress(Math.min(elapsed / duration, 1));
    }, 50);
    hintTimerRef.current = setTimeout(() => {
      if (hintIntervalRef.current) {
        clearInterval(hintIntervalRef.current);
        hintIntervalRef.current = null;
      }
      setHintProgress(0);
      setHintIndex(prev => prev + 1);
    }, duration);
  }, [clearHintTimer, hintIndex]);

  const puzzleId = state.activePuzzle;
  if (!puzzleId) return null;

  const puzzle = PUZZLES[puzzleId];
  if (!puzzle) return null;

  const checkAnswer = () => {
    const trimmed = input.trim();
    let correct = false;

    if (puzzle.acceptedAnswers) {
      correct = puzzle.acceptedAnswers.includes(trimmed);
    } else if (Array.isArray(puzzle.solution)) {
      // For sequence puzzles, check if space-separated input matches
      const inputParts = trimmed.split(/[\s,]+/);
      correct = inputParts.length === puzzle.solution.length &&
        inputParts.every((part, i) => part === puzzle.solution[i]);
    } else {
      correct = trimmed === puzzle.solution;
    }

    if (correct) {
      setIsCorrect(true);
      actions.solvePuzzle(puzzleId);
      if (puzzle.reward) {
        actions.addItem(puzzle.reward);
        const item = ITEMS[puzzle.reward];
        actions.addMessage(`${item.icon} ${item.name}を手に入れた！`);
        actions.showAcquiredItem(puzzle.reward);
      }
      const storyText = puzzle.choiceStoryTexts?.[trimmed] || puzzle.storyText || '正解！';
      setResultText(storyText);
      setShowResult(true);
    } else {
      setShakeWrong(true);
      setTimeout(() => setShakeWrong(false), 500);
      actions.addMessage('……違うようだ。');
    }
  };

  const handleClose = () => {
    if (isCorrect) {
      // Handle special puzzles
      if (puzzleId === 'final_choice') {
        actions.closePuzzle();
        actions.determineEnding(input.trim());
        return;
      }
      if (puzzleId === 'last_words') {
        actions.addMessage('選択は記録された。');
      }
    }
    setInput('');
    setHintIndex(-1);
    setShowResult(false);
    setResultText('');
    setIsCorrect(false);
    actions.closePuzzle();
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0, 0, 0, 0.85)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 100,
      padding: '1rem',
    }}>
      <div style={{
        background: 'linear-gradient(180deg, #1a1a1a 0%, #111 100%)',
        border: '1px solid #333',
        borderRadius: '8px',
        padding: 'clamp(1rem, 4vw, 2rem)',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '85vh',
        overflow: 'auto',
        fontFamily: '"Noto Serif JP", serif',
        animation: shakeWrong ? 'shakeX 0.5s' : undefined,
      }}>
        {!showResult ? (
          <>
            <h2 style={{
              color: '#c8a87c',
              fontSize: 'clamp(1.2rem, 3.5vw, 1.5rem)',
              marginBottom: '1rem',
              letterSpacing: '0.2em',
              borderBottom: '1px solid #333',
              paddingBottom: '0.5rem',
            }}>
              {puzzle.title}
            </h2>

            <div style={{
              color: '#bbb',
              fontSize: 'clamp(1rem, 3vw, 1.15rem)',
              lineHeight: 2,
              marginBottom: '1.5rem',
              whiteSpace: 'pre-line',
            }}>
              {puzzle.description}
            </div>

            {puzzle.table && !state.inventory.includes('morse_chart') && (
              <div style={{
                color: '#888',
                fontSize: 'clamp(0.9rem, 2.8vw, 1.05rem)',
                lineHeight: 1.8,
                marginBottom: '1.5rem',
                padding: '0.8rem',
                background: 'rgba(100, 100, 100, 0.1)',
                border: '1px solid #333',
                borderRadius: '4px',
                fontStyle: 'italic',
              }}>
                変換表があれば解読できるかもしれない……
              </div>
            )}

            {puzzle.table && state.inventory.includes('morse_chart') && (
              <div style={{
                overflowX: 'auto',
                marginBottom: '1.5rem',
              }}>
                <table style={{
                  borderCollapse: 'collapse',
                  width: '100%',
                  fontSize: 'clamp(0.75rem, 2.5vw, 0.95rem)',
                  fontFamily: 'monospace',
                }}>
                  <thead>
                    <tr>
                      {puzzle.table.headers.map((h, i) => (
                        <th key={i} style={{
                          border: '1px solid #444',
                          padding: '0.3rem 0.5rem',
                          color: '#c8a87c',
                          background: 'rgba(200, 168, 124, 0.1)',
                          whiteSpace: 'nowrap',
                        }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {puzzle.table.rows.map((row, ri) => (
                      <tr key={ri}>
                        {row.map((cell, ci) => (
                          <td key={ci} style={{
                            border: '1px solid #333',
                            padding: '0.3rem 0.5rem',
                            color: ci === 0 ? '#c8a87c' : '#bbb',
                            background: ci === 0 ? 'rgba(200, 168, 124, 0.1)' : 'transparent',
                            textAlign: 'center',
                            whiteSpace: 'nowrap',
                          }}>
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Hints */}
            {hintIndex >= 0 && (
              <div style={{
                margin: '1rem 0',
                padding: '0.8rem',
                background: 'rgba(200, 168, 124, 0.1)',
                border: '1px solid rgba(200, 168, 124, 0.3)',
                borderRadius: '4px',
              }}>
                {puzzle.hints.slice(0, hintIndex + 1).map((hint, i) => (
                  <p key={i} style={{
                    color: '#c8a87c',
                    fontSize: '1rem',
                    margin: '0.3rem 0',
                    lineHeight: 1.6,
                  }}>
                    ヒント{i + 1}: {hint}
                  </p>
                ))}
              </div>
            )}

            {/* Input */}
            <div style={{ marginBottom: '1rem' }}>
              <input
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.nativeEvent.isComposing && checkAnswer()}
                placeholder="答えを入力..."
                autoFocus
                style={{
                  width: '100%',
                  padding: '0.8rem',
                  fontSize: '1.15rem',
                  background: '#0a0a0a',
                  border: '1px solid #444',
                  borderRadius: '4px',
                  color: '#eee',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                  outline: 'none',
                }}
              />
            </div>

            {/* Buttons */}
            <div style={{
              display: 'flex',
              gap: '0.8rem',
              flexWrap: 'wrap',
            }}>
              <button onClick={checkAnswer} style={btnStyle('#c8a87c')}>
                回答
              </button>
              {hintIndex < puzzle.hints.length - 1 && (
                <button
                  onMouseDown={startHintTimer}
                  onMouseUp={clearHintTimer}
                  onMouseLeave={clearHintTimer}
                  onTouchStart={startHintTimer}
                  onTouchEnd={clearHintTimer}
                  onTouchCancel={clearHintTimer}
                  style={{
                    ...btnStyle('#888'),
                    position: 'relative',
                    overflow: 'hidden',
                    userSelect: 'none',
                  }}
                >
                  {hintProgress > 0 && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      height: '100%',
                      width: `${hintProgress * 100}%`,
                      background: 'rgba(200, 168, 124, 0.25)',
                      transition: 'width 0.05s linear',
                    }} />
                  )}
                  <span style={{ position: 'relative', zIndex: 1 }}>
                    {(() => {
                      const sec = hintIndex + 1 === 2 ? 10 : 5;
                      return hintProgress > 0
                        ? `長押し中... ${Math.ceil(sec - hintProgress * sec)}秒`
                        : `ヒント (${hintIndex + 1}/${puzzle.hints.length}) - 長押し${sec}秒`;
                    })()}
                  </span>
                </button>
              )}
              <button onClick={handleClose} style={btnStyle('#555')}>
                戻る
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={{
              color: '#ccc',
              fontSize: 'clamp(1rem, 3vw, 1.2rem)',
              lineHeight: 2.2,
              whiteSpace: 'pre-line',
              marginBottom: '2rem',
            }}>
              {resultText}
            </div>

            <button onClick={handleClose} style={btnStyle('#c8a87c')}>
              閉じる
            </button>
          </>
        )}
      </div>

      <style>{`
        @keyframes shakeX {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-10px); }
          40% { transform: translateX(10px); }
          60% { transform: translateX(-5px); }
          80% { transform: translateX(5px); }
        }

      `}</style>
    </div>
  );
};

function btnStyle(color: string): React.CSSProperties {
  return {
    padding: '0.6rem 1.5rem',
    fontSize: '1.05rem',
    background: 'transparent',
    border: `1px solid ${color}`,
    color: color,
    cursor: 'pointer',
    fontFamily: '"Noto Serif JP", serif',
    letterSpacing: '0.2em',
    borderRadius: '4px',
    transition: 'all 0.2s',
  };
}
